import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getNotifications, triggerEscalationCheck, getNotificationStats, resendNotification, runWeeklyOfferDigest, sendTestOfferDigest } from '@/lib/api';
import { AlertTriangle, Mail, CheckCircle, XCircle, RefreshCw, Loader2, Bell, Inbox, Factory, ChevronLeft, ChevronRight, Send, CalendarClock } from 'lucide-react';

export const NotificationsTab = ({ t }) => {
  const [notifications, setNotifications] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(null);
  const [page, setPage] = useState(1);
  const [sendingTestDigest, setSendingTestDigest] = useState(false);
  const [runningDigest, setRunningDigest] = useState(false);

  const loadNotifs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [notifsRes, statsRes] = await Promise.all([
        getNotifications({ page: p, limit: 10 }),
        getNotificationStats()
      ]);
      setNotifications(notifsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNotifs(page); }, [page, loadNotifs]);

  const handleTriggerCheck = async () => {
    setChecking(true);
    try {
      const res = await triggerEscalationCheck();
      toast.success(`${t('admin.triggerCheckSuccess')}: ${res.data.sent_count} E-Mails`);
      loadNotifs(page);
    } catch {
      toast.error(t('admin.triggerCheckFailed'));
    } finally {
      setChecking(false);
    }
  };

  const handleSendTestDigest = async () => {
    setSendingTestDigest(true);
    try {
      const res = await sendTestOfferDigest();
      toast.success(`Test-Wochenüberblick gesendet an ${res.data.recipient_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test-E-Mail fehlgeschlagen');
    } finally {
      setSendingTestDigest(false);
    }
  };

  const handleRunDigest = async () => {
    setRunningDigest(true);
    try {
      const res = await runWeeklyOfferDigest();
      toast.success(`Wochenüberblick ausgeführt: ${res.data.emails_sent} E-Mail(s) versendet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ausführung fehlgeschlagen');
    } finally {
      setRunningDigest(false);
    }
  };

  const handleResend = async (notificationId) => {    setResending(notificationId);
    try {
      const res = await resendNotification(notificationId);
      toast.success(res.data.message);
      loadNotifs(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erneut senden fehlgeschlagen');
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="notification-stats-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-orange-500" />{t('admin.notificationsTitle')}</CardTitle>
            <CardDescription>{t('admin.notificationsDesc')}</CardDescription>
          </div>
          <Button onClick={handleTriggerCheck} disabled={checking} className="bg-orange-500 hover:bg-orange-600" data-testid="trigger-check-btn">
            {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('admin.triggerCheck')}
          </Button>
        </CardHeader>
        <CardContent>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
              <div className="p-4 rounded-lg border bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground">{t('admin.totalNotifications')}</p>
                <p className="text-2xl font-bold" data-testid="total-notifications">{stats.total_notifications}</p>
              </div>
              <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-400">{t('admin.notifTypeNewRequest')}</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="new-request-count">{stats.new_request_count || 0}</p>
              </div>
              <div className="p-4 rounded-lg border bg-orange-50 dark:bg-orange-950/20 border-orange-200">
                <p className="text-xs font-medium text-orange-700 dark:text-orange-400">{t('admin.notifTypeEscalation')}</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="escalation-count">{stats.escalation_count || 0}</p>
              </div>
              <div className="p-4 rounded-lg border bg-purple-50 dark:bg-purple-950/20 border-purple-200">
                <p className="text-xs font-medium text-purple-700 dark:text-purple-400">{t('admin.notifTypeExtCompany') || 'Ext. Firmen'}</p>
                <p className="text-2xl font-bold text-purple-600" data-testid="ext-company-count">{stats.ext_company_count || 0}</p>
              </div>
              <div className="p-4 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200">
                <p className="text-xs font-medium text-green-700 dark:text-green-400">{t('admin.emailsSent')}</p>
                <p className="text-2xl font-bold text-green-600" data-testid="emails-sent">{stats.emails_sent}</p>
              </div>
              <div className={`p-4 rounded-lg border ${stats.emails_failed > 0 ? 'bg-red-50 dark:bg-red-950/20 border-red-200' : 'bg-muted/30'}`}>
                <p className="text-xs font-medium text-muted-foreground">{t('admin.emailsFailed')}</p>
                <p className={`text-2xl font-bold ${stats.emails_failed > 0 ? 'text-red-600' : ''}`} data-testid="emails-failed">{stats.emails_failed}</p>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{t('admin.triggerCheckDesc')}</p>
        </CardContent>
      </Card>

      <Card data-testid="weekly-offer-digest-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-orange-500" />
            Angebots-Wochenüberblick
          </CardTitle>
          <CardDescription>
            Käufer erhalten montags eine Sammel-E-Mail „X neue Angebote diese Woche — jetzt vergleichen"
            mit Direkt-Links in den Vergleichsdialog. Hier manuell auslösbar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleSendTestDigest}
              disabled={sendingTestDigest}
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
              data-testid="send-test-digest-btn"
            >
              {sendingTestDigest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Test-Mail an mich senden
            </Button>
            <Button
              onClick={handleRunDigest}
              disabled={runningDigest}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="run-weekly-digest-btn"
            >
              {runningDigest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Jetzt für alle Käufer ausführen
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            „Test-Mail an mich" sendet einen Beispiel-Digest an Ihre Admin-Adresse (immer befüllt).
            „Für alle ausführen" verschickt echte Überblicke an Käufer mit neuen Angeboten der letzten 7 Tage.
          </p>
        </CardContent>
      </Card>

      <Card data-testid="notification-history-card">
        <CardHeader>
          <CardTitle>{t('admin.notifRecipients')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
          ) : notifications.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('admin.noNotifications')}</p>
          ) : (
            <div className="space-y-3" data-testid="notification-list">
              {notifications.items.map((notif) => {
                const isNewRequest = notif.type === 'new_request';
                const isExtStatus = notif.type === 'external_company_status';
                return (
                <div key={notif.notification_id} className="p-4 border rounded-lg" data-testid={`notif-row-${notif.notification_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isNewRequest ? (
                        <Inbox className="h-4 w-4 text-blue-500 shrink-0" />
                      ) : isExtStatus ? (
                        <Factory className="h-4 w-4 text-purple-500 shrink-0" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                      )}
                      <span className="font-semibold text-sm">
                        {isExtStatus ? notif.company_name : notif.material_title}
                      </span>
                      {!isExtStatus && <Badge variant="outline" className="text-xs">{notif.sku_article}</Badge>}
                      <Badge className={`text-xs ${
                        isNewRequest ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                        isExtStatus ? (notif.status_change === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400') :
                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`} data-testid="notif-type-badge">
                        {isExtStatus
                          ? (notif.status_change === 'approved' ? (t('admin.notifExtApproved') || 'Genehmigt') : (t('admin.notifExtRejected') || 'Abgelehnt'))
                          : isNewRequest ? t('admin.notifTypeNewRequest') : t('admin.notifTypeEscalation')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResend(notif.notification_id)}
                        disabled={resending === notif.notification_id}
                        className="h-7 px-2 text-xs"
                        data-testid={`resend-btn-${notif.notification_id}`}
                      >
                        {resending === notif.notification_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        {t('admin.resendEmail') || 'Erneut senden'}
                      </Button>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(notif.sent_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {isExtStatus ? (
                    <div className="text-xs text-muted-foreground">
                      <span>{t('admin.notifExtCompany') || 'Externe Firma'}:</span> <span className="font-medium">{notif.company_name}</span>
                    </div>
                  ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div><span className="text-muted-foreground">{t('admin.notifRequester')}:</span> <span className="font-medium">{notif.requester_name}</span></div>
                    <div><span className="text-muted-foreground">{t('admin.notifOwner')}:</span> <span className="font-medium">{notif.owner_name}</span></div>
                    <div><span className="text-muted-foreground">{t('admin.notifSite')}:</span> <span className="font-medium">{notif.site_name}</span></div>
                    {isNewRequest ? (
                      <div><span className="text-muted-foreground">{t('admin.notifRequestedQty')}:</span> <span className="font-medium">{notif.requested_qty}</span></div>
                    ) : (
                      <div><span className="text-muted-foreground">{t('admin.notifDaysPending')}:</span> <span className="font-medium text-red-600">{notif.days_pending}d</span></div>
                    )}
                  </div>
                  )}
                  {notif.recipients && notif.recipients.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {notif.recipients.map((r, i) => (
                        <Badge key={i} variant={r.status === 'sent' ? 'default' : 'destructive'} className="text-xs gap-1">
                          <Mail className="h-3 w-3" />
                          {r.email} ({r.role === 'owner' ? t('admin.notifOwner') : r.role === 'requester' ? t('admin.notifRequester') : 'Admin'})
                          {r.status === 'sent' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {notifications.pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="notif-prev-page">
                <ChevronLeft className="h-4 w-4 mr-1" />{t('admin.auditPrevPage')}
              </Button>
              <span className="text-sm text-muted-foreground">{t('admin.auditPage', { page: notifications.page, pages: notifications.pages })}</span>
              <Button variant="outline" size="sm" disabled={page >= notifications.pages} onClick={() => setPage(p => p + 1)} data-testid="notif-next-page">
                {t('admin.auditNextPage')}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
